import { useState } from 'react';
import { FileDown, Inbox, Lock } from 'lucide-react';
import { useMyEmployee, useMyPayslips } from '@/composables/payroll/useSelfService';
import { useExport } from '@/composables/shared/useExport';
import { selfServiceService } from '@/services/payroll.service';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { formatCurrency, formatDateUTC } from '@/lib/formatters';
import { MONTH_OPTIONS, PAYSLIP_STATUS_STYLES_SELF, type Payslip } from '@/types/payroll';

/**
 * An employee's own payslips.
 *
 * Deliberately read-only and free of any employee picker — the server scopes
 * every request to the login's own employee record, and only issued payslips
 * are ever returned.
 */
export function MyPayslipsPage() {
  const [page, setPage] = useState(1);
  const { employee, isLoading: empLoading } = useMyEmployee();
  const { payslips, meta, isLoading, error } = useMyPayslips({ page, limit: 20 });
  const { exportFile, isExporting } = useExport();

  if (empLoading || isLoading) return <LoadingSpinner />;

  // 403 here means the login has no employee record behind it.
  if (error?.response?.status === 403) {
    return (
      <PageContainer title="My Payslips">
        <EmptyState
          icon={Lock}
          title="No employee record linked to your account"
          description={error.response.data?.message
            ?? 'Ask an administrator to link your login to your employee record.'}
        />
      </PageContainer>
    );
  }

  const download = (p: Payslip) =>
    exportFile(() => selfServiceService.myPayslipPdf(p.id), `payslip-${p.payslipNumber}.pdf`);

  return (
    <PageContainer
      title="My Payslips"
      description={employee
        ? `${employee.firstName} ${employee.lastName} · ${employee.employeeCode}`
        : undefined}
    >
      {payslips.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No payslips yet"
          description="Payslips appear here once payroll has been run and published for a month you worked."
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Issued payslips</CardTitle>
            <p className="text-sm text-muted-foreground">
              {meta?.total ?? payslips.length} payslip{(meta?.total ?? 1) === 1 ? '' : 's'} on record.
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Payslip #</TableHead>
                  <TableHead>Pay date</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net pay</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      {MONTH_OPTIONS[p.periodMonth - 1]?.label} {p.periodYear}
                      {Number(p.lopDays) > 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {Number(p.lopDays)} day{Number(p.lopDays) === 1 ? '' : 's'} loss of pay
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.payslipNumber}</TableCell>
                    <TableCell>{formatDateUTC(p.payDate)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(p.grossEarnings, p.currency)}</TableCell>
                    <TableCell className="text-right tabular-nums">− {formatCurrency(p.totalDeductions, p.currency)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatCurrency(p.netPay, p.currency)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`border-0 font-medium ${PAYSLIP_STATUS_STYLES_SELF[p.status] ?? ''}`}>
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" disabled={isExporting} onClick={() => download(p)}>
                        <FileDown className="mr-2 h-3.5 w-3.5" />PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {(meta?.totalPages ?? 1) > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Page {meta?.page} of {meta?.totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={page >= (meta?.totalPages ?? 1)} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
